import { AlgoType, HedgeMechanism } from '@/types/generated/algo'
import { PlanLevel, StreamKind } from '@/types/generated/common/account'
import { AnalyticsType, TradeVenue } from '@/types/generated/common/types'
import { Capabilities, Plan } from '@/types/generated/public_api'
import { TriggerConditionType } from '@/types/generated/triggers'

/**
 * The $0 tier's `plan_label`. The backend renamed this token from "FREE" to
 * "ESSENTIAL" (oems_v2 #463) and the two repos cannot deploy in the same
 * instant, so BOTH have to read as the free tier for the length of that window.
 * Getting it wrong is not cosmetic: the badge turns accent (reads as paid) and
 * the post-checkout poll reports success before the webhook has landed.
 *
 * Use `isFreePlanLabel`, never a bare comparison. Drop the legacy token once
 * the backend rename has shipped to prod.
 */
export const PLAN_LABEL_FREE = 'ESSENTIAL'
const PLAN_LABEL_FREE_LEGACY = 'FREE'

export function isFreePlanLabel(label: string | null | undefined): boolean {
  return label === PLAN_LABEL_FREE || label === PLAN_LABEL_FREE_LEGACY
}

/**
 * Named feature flags carried in `Capabilities.allowedFeatures`. These mirror the
 * backend's entitlement strings — compare against these constants, never bare
 * string literals scattered through the UI.
 */
export const BillingFeature = {
  RiskSimulation: 'risk_simulation',
  AlgoBenchmarks: 'algo_benchmarks',
  Paradigm: 'paradigm',
  CrossExchangeCharts: 'cross_exchange_charts',
  // Declarative keys: the backend lists them so `get_plans` can describe a plan,
  // but gates nothing on them (oems_v2 `DECLARATIVE_FEATURES`). Display only.
  Newsfeed: 'newsfeed',
  EconomicCalendar: 'economic_calendar',
  AiPromptTrading: 'ai_prompt_trading',
  AiResearchAgents: 'ai_research_agents',
  DataApi: 'data_api',
  ArbitrageScanner: 'arbitrage_scanner',
} as const

export type BillingFeature = (typeof BillingFeature)[keyof typeof BillingFeature]

/**
 * Pure capability checks resolved from a `Capabilities` message (from
 * `get_subscription` for the current user, or `get_plans` per plan). All are
 * fail-closed: an absent `capabilities` denies. Callers that want to stay
 * permissive while the subscription is still loading (so a paying user never sees
 * a Pro control flicker as locked) should gate on whether capabilities are
 * present — see `useCapabilities`. The backend remains the real enforcement.
 */
export function isAlgoAllowed(caps: Capabilities | undefined, algo: AlgoType): boolean {
  return caps?.allowedAlgos?.includes(algo) ?? false
}

export function isHedgeMechanismAllowed(
  caps: Capabilities | undefined,
  mechanism: HedgeMechanism
): boolean {
  return caps?.allowedHedgeMechanisms?.includes(mechanism) ?? false
}

export function isAnalyticsTypeAllowed(
  caps: Capabilities | undefined,
  type: AnalyticsType
): boolean {
  return caps?.allowedAnalyticsTypes?.includes(type) ?? false
}

export function isVenueAllowed(caps: Capabilities | undefined, venue: TradeVenue): boolean {
  return caps?.allowedVenues?.includes(venue) ?? false
}

export function isTriggerConditionAllowed(
  caps: Capabilities | undefined,
  type: TriggerConditionType
): boolean {
  return caps?.allowedTriggerConditions?.includes(type) ?? false
}

export function isStreamKindAllowed(caps: Capabilities | undefined, kind: StreamKind): boolean {
  return caps?.allowedStreamKinds?.includes(kind) ?? false
}

export function hasFeature(caps: Capabilities | undefined, feature: BillingFeature): boolean {
  return caps?.allowedFeatures?.includes(feature) ?? false
}

export function getMaxAccounts(caps: Capabilities | undefined): number {
  return caps?.maxAccounts ?? 0
}

export function getMaxSharedMembers(caps: Capabilities | undefined): number {
  return caps?.maxSharedMembers ?? 0
}

/**
 * Upgrade ordering of the plan tiers — deliberately NOT the `PlanLevel` enum's
 * numeric order: BASIC was added later as `4` but sits between FREE (`1`) and
 * PRO (`2`). Used to pick the *cheapest* plan that unlocks a gated capability so
 * a locked control can say "Requires Basic" rather than over-selling "Pro".
 * Unknown/unspecified levels rank LAST so a malformed catalog entry (ts-proto
 * defaults a missing `level` to `UNSPECIFIED = 0`) can never sort ahead of a
 * real tier and get surfaced as the upgrade target.
 */
export function planLevelOrder(level: PlanLevel): number {
  switch (level) {
    case PlanLevel.PLAN_LEVEL_FREE:
      return 1
    case PlanLevel.PLAN_LEVEL_BASIC:
      return 2
    case PlanLevel.PLAN_LEVEL_PRO:
      return 3
    case PlanLevel.PLAN_LEVEL_ENTERPRISE:
      return 4
    default:
      return Number.MAX_SAFE_INTEGER
  }
}

/**
 * The cheapest plan in the public `get_plans` catalog whose capabilities satisfy
 * `predicate`. Drives the "Requires <plan>" hint on a control the current plan
 * can't use — read from the backend catalog rather than hardcoding the tier
 * table, so it can't drift from enforcement. Undefined when no plan satisfies it
 * (the capability is genuinely unavailable, not merely an upgrade away).
 */
export function lowestPlanWhere(
  plans: Plan[],
  predicate: (caps: Capabilities) => boolean
): Plan | undefined {
  return plans
    .filter((plan) => plan.capabilities !== undefined && predicate(plan.capabilities))
    .sort((a, b) => planLevelOrder(a.level) - planLevelOrder(b.level))[0]
}
